import { useEffect, useState } from "react";
import { CloudUpload, HardDrive } from "lucide-react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { resolveMigration } from "@/lib/cloudSync";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

// Asks the user, once per device, whether to import locally-stored Chronicles
// into their account after login. Never silently overwrites cloud data or
// deletes local data.
export default function MigrationDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ localEvents: 0, localTasks: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onNeeded = (e) => {
      setCounts(e.detail || { localEvents: 0, localTasks: 0 });
      setOpen(true);
    };
    window.addEventListener("chronicles:migration-needed", onNeeded);
    return () => window.removeEventListener("chronicles:migration-needed", onNeeded);
  }, []);

  const choose = async (choice) => {
    setBusy(true);
    try {
      await resolveMigration(choice);
      if (choice === "import") toast.success(t("migration.imported"));
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="rounded-[20px]" data-testid="migration-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-slate-500" />
            {t("migration.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("migration.body", { events: counts.localEvents, tasks: counts.localTasks })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            className="rounded-full h-11 w-full justify-center gap-2"
            onClick={() => choose("import")}
            disabled={busy}
            data-testid="migration-import"
          >
            <CloudUpload className="h-4 w-4" />
            {t("migration.import")}
          </Button>
          <Button
            variant="outline"
            className="rounded-full h-11 w-full border-slate-300"
            onClick={() => choose("keepLocal")}
            disabled={busy}
            data-testid="migration-keep-local"
          >
            {t("migration.keepLocal")}
          </Button>
          <Button
            variant="ghost"
            className="rounded-full h-11 w-full text-slate-500"
            onClick={() => choose("cancel")}
            disabled={busy}
            data-testid="migration-cancel"
          >
            {t("migration.cancel")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
